// The four drafted-configuration steps (prototype stepBody 'skill'/'bindings'/'lifecycle'/'actions'):
// the Skill rendered VERBATIM (goal · constraints · playbook), the declared pulls as chips with the
// subject key highlighted first, the lifecycle chips with the two exits and the validity line, and
// the action palette with gated (amber) vs autonomous chips. Every value comes from the projection
// (G-182 — the anti-omission posture, brief §3.1); variants derive from projection flags. The
// "add a pull" affordance surfaces an honest v1 scope note — it registers nothing.

import { Fragment, useState } from 'react';
import { Icon } from '../../../../app/Icon.tsx';
import type { ComposerPaletteRow, ComposerSkillView } from '../../projections.ts';
import { displayState } from '../composer-model.ts';
import { Accent, ConfigChip, FieldLabel, WhyLine } from '../composer-ui.tsx';

/** The workflow Skill, verbatim (goal in the agent voice; constraints and playbook labelled). */
export function SkillStep({ skill }: { readonly skill: ComposerSkillView }): React.JSX.Element {
  return (
    <div>
      <p className="ritona-voice max-w-prose text-lg leading-relaxed text-text">{skill.goal}</p>
      <div className="mt-4">
        <FieldLabel>Constraints</FieldLabel>
      </div>
      <p className="mt-1 max-w-prose text-sm leading-relaxed text-text-muted">
        {skill.constraints}
      </p>
      <div className="mt-4">
        <FieldLabel>Playbook</FieldLabel>
      </div>
      <p className="mt-1 max-w-prose text-sm leading-relaxed text-text-muted">{skill.playbook}</p>
      <WhyLine>
        Stored on the definition, versioned with it, rendered verbatim at the gate. The worker gets
        this plus a minimal Worker Preamble — <Accent>not the manager’s charter</Accent>.
      </WhyLine>
    </div>
  );
}

/** The declared pulls as chips — subject key first and highlighted (manifest: subject key first). */
export function BindingsStep({
  bindings,
}: {
  readonly bindings: readonly string[];
}): React.JSX.Element {
  const [note, setNote] = useState<string | null>(null);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {bindings.map((binding, index) => (
          <ConfigChip key={binding} hook="binding" variant={index === 0 ? 'key' : 'plain'}>
            {binding}
          </ConfigChip>
        ))}
        <button
          type="button"
          aria-label="Add a pull"
          onClick={() => setNote('v1: item-scoped, registrable queries only.')}
          className="ritona-numeric inline-flex items-center gap-1.5 rounded-sm border border-dashed border-border bg-surface px-2.5 py-1 text-xs text-text-muted hover:border-sage hover:text-forest"
        >
          add a pull
        </button>
      </div>
      {note !== null ? (
        <p role="status" className="ritona-numeric mt-3 text-xs text-text-muted">
          {note}
        </p>
      ) : null}
      <WhyLine>
        The declared pulls — <Accent>the platform runs them</Accent>, not the model, so the dispute
        email can never be quietly omitted. Items materialise from the subject key.
      </WhyLine>
    </div>
  );
}

export interface LifecycleStepProps {
  readonly lifecycle: {
    readonly states: readonly string[];
    readonly goalExit: string;
    readonly deadExit: string;
  };
}

/** The lifecycle chips, the two exits, and the validity line (validated at activation). */
export function LifecycleStep({ lifecycle }: LifecycleStepProps): React.JSX.Element {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {lifecycle.states.map((state, index) => (
          <Fragment key={state}>
            {index > 0 ? <span className="ritona-numeric text-xs text-text-subtle">→</span> : null}
            <ConfigChip hook="state">{displayState(state)}</ConfigChip>
          </Fragment>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <FieldLabel>exits</FieldLabel>
        <ConfigChip
          hook="exit"
          variant="goal"
        >{`✓ ${displayState(lifecycle.goalExit)}`}</ConfigChip>
        <ConfigChip
          hook="exit"
          variant="dead"
        >{`⊘ ${displayState(lifecycle.deadExit)} · gated`}</ConfigChip>
      </div>
      <p className="mt-5 inline-flex max-w-prose items-start gap-2 rounded-md border border-sage bg-forest-tint px-4 py-2 text-sm text-forest">
        <span className="mt-0.5 shrink-0">
          <Icon name="check" size={13} />
        </span>
        <span>
          Valid lifecycle — exactly one goal exit (<b>{displayState(lifecycle.goalExit)}</b>) and
          one dead exit (<b>{displayState(lifecycle.deadExit)}</b>).
        </span>
      </p>
      <WhyLine>
        Validated at activation. Without the dead exit, columns clog and the board stops telling the
        truth about flow.
      </WhyLine>
    </div>
  );
}

/** The action palette: amber chips are gated (stop for a verdict), the rest run autonomously. */
export function PaletteStep({
  palette,
}: {
  readonly palette: readonly ComposerPaletteRow[];
}): React.JSX.Element {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {palette.map((row) => (
          <ConfigChip key={row.label} hook="palette" variant={row.gated ? 'gated' : 'plain'}>
            {row.label}
          </ConfigChip>
        ))}
      </div>
      <WhyLine>
        Each type maps to an owning module, a capability, a payload render, and a declared
        post-execution move. <Accent>Amber = gated</Accent>: anything that touches the world stops
        for a verdict.
      </WhyLine>
    </div>
  );
}
